import { LANGUAGE_CODES } from '@talkvibe/shared';

/**
 * Presentation-side language helpers.
 *
 * Actual translation happens server-side - the provider's API key must never
 * reach the browser. What lives here is only the naming of languages, done
 * with Intl.DisplayNames so the list stays localised without us shipping a
 * translated table of language names.
 */

const displayNameCache = new Map<string, Intl.DisplayNames>();

function displayNames(locale: string, type: 'language' | 'region'): Intl.DisplayNames {
  const key = `${locale}:${type}`;
  let instance = displayNameCache.get(key);
  if (!instance) {
    instance = new Intl.DisplayNames([locale, 'en'], { type, fallback: 'code' });
    displayNameCache.set(key, instance);
  }
  return instance;
}

/** Human-readable language name, e.g. 'th' -> 'Thai'. */
export function languageName(code: string, locale = 'en'): string {
  try {
    return displayNames(locale, 'language').of(code) ?? code;
  } catch {
    return code;
  }
}

/** Human-readable country name, e.g. 'TH' -> 'Thailand'. */
export function countryName(code: string, locale = 'en'): string {
  try {
    return displayNames(locale, 'region').of(code) ?? code;
  } catch {
    return code;
  }
}

/** The supported languages, named and sorted for a select element. */
export function languageOptions(locale = 'en'): Array<{ value: string; label: string }> {
  return LANGUAGE_CODES.map((code) => ({ value: code, label: languageName(code, locale) })).sort(
    (a, b) => a.label.localeCompare(b.label, locale),
  );
}

/**
 * The first of the visitor's browser languages we can carry content in.
 *
 * Distinct from `browserLanguage()` in the i18n module, which answers with one
 * of the two *interface* catalogues. This answers with one of the 44 languages
 * the product can translate into, which is a much longer list — so a Japanese
 * browser gets `ja` here and `en` there, and both are right for what they are
 * asked.
 *
 * Used to seed the language field on sign-up. It is the visitor's own setting,
 * which beats anything inferred from their address.
 */
export function browserContentLanguage(): string | null {
  if (typeof navigator === 'undefined') return null;
  const supported = new Set(LANGUAGE_CODES);

  for (const candidate of navigator.languages ?? [navigator.language]) {
    if (!candidate) continue;
    // `zh-TW` is a code in its own right here, so try the whole tag before
    // reducing it to `zh` — the two are different catalogues of content.
    const exact = LANGUAGE_CODES.find((code) => code.toLowerCase() === candidate.toLowerCase());
    if (exact !== undefined) return exact;

    const base = candidate.toLowerCase().split('-')[0] ?? '';
    if (supported.has(base)) return base;
  }
  return null;
}
